// =====================================================================
// i18n Configuration
// Description: Internationalization setup using react-i18next
// Location: src/i18n/config.js
// =====================================================================

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enUS from './locales/en-US.json';
import enGB from './locales/en-GB.json';
import esES from './locales/es-ES.json';
import esMX from './locales/es-MX.json';
import frFR from './locales/fr-FR.json';
import deDE from './locales/de-DE.json';
import itIT from './locales/it-IT.json';
import ptBR from './locales/pt-BR.json';
import jaJP from './locales/ja-JP.json';
import koKR from './locales/ko-KR.json';
import zhCN from './locales/zh-CN.json';
import zhTW from './locales/zh-TW.json';

// Supported languages configuration
export const SUPPORTED_LANGUAGES = [
  { code: 'en-US', name: 'English (US)', nativeName: 'English (US)' },
  { code: 'en-GB', name: 'English (UK)', nativeName: 'English (UK)' },
  { code: 'es-ES', name: 'Spanish (Spain)', nativeName: 'Español (España)' },
  { code: 'es-MX', name: 'Spanish (Mexico)', nativeName: 'Español (México)' },
  { code: 'fr-FR', name: 'French', nativeName: 'Français' },
  { code: 'de-DE', name: 'German', nativeName: 'Deutsch' },
  { code: 'it-IT', name: 'Italian', nativeName: 'Italiano' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', nativeName: 'Português (Brasil)' },
  { code: 'ja-JP', name: 'Japanese', nativeName: '日本語' },
  { code: 'ko-KR', name: 'Korean', nativeName: '한국어' },
  { code: 'zh-CN', name: 'Chinese (Simplified)', nativeName: '中文 (简体)' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '中文 (繁體)' }
];

// Translation resources
const resources = {
  'en-US': { translation: enUS },
  'en-GB': { translation: enGB },
  'es-ES': { translation: esES },
  'es-MX': { translation: esMX },
  'fr-FR': { translation: frFR },
  'de-DE': { translation: deDE },
  'it-IT': { translation: itIT },
  'pt-BR': { translation: ptBR },
  'ja-JP': { translation: jaJP },
  'ko-KR': { translation: koKR },
  'zh-CN': { translation: zhCN },
  'zh-TW': { translation: zhTW }
};

// Custom language detector order
const detectionOptions = {
  // Order of detection methods
  order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
  
  // Keys to lookup language from
  lookupQuerystring: 'lng',
  lookupLocalStorage: 'i18nextLng',
  
  // Cache user language on
  caches: ['localStorage'],
  
  // Only detect languages that are in the whitelist
  checkWhitelist: true
};

// Initialize i18next
i18n
  .use(LanguageDetector) // Detect user language
  .use(initReactI18next) // Pass i18n instance to react-i18next
  .init({
    resources,
    fallbackLng: 'en-US', // Fallback language
    defaultNS: 'translation',
    
    // Supported languages
    supportedLngs: SUPPORTED_LANGUAGES.map(lang => lang.code),
    
    // Language detection options
    detection: detectionOptions,
    
    // Performance optimization
    load: 'currentOnly', // Only load current language
    preload: [], // Don't preload any languages
    
    // React-specific options
    react: {
      useSuspense: false, // Disable suspense mode for better compatibility
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      transEmptyNodeValue: '', // What to return for empty Trans
      transSupportBasicHtmlNodes: true, // Allow basic HTML in translations
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p', 'span'], // Allowed HTML tags
    },
    
    // Interpolation options
    interpolation: {
      escapeValue: false, // React already escapes values
      formatSeparator: ',',
      format: (value, format, lng) => {
        // Custom formatters can be added here
        if (format === 'uppercase') return value.toUpperCase();
        if (format === 'lowercase') return value.toLowerCase();
        if (value instanceof Date) {
          return new Intl.DateTimeFormat(lng).format(value);
        }
        return value;
      }
    },
    
    // Development options
    debug: process.env.NODE_ENV === 'development',
    
    // Missing key handling
    saveMissing: false, // Don't save missing keys
    missingKeyHandler: (lngs, ns, key, fallbackValue) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Missing translation key: ${key} for languages: ${lngs.join(', ')}`);
      }
    },
    
    // Return empty string for missing keys instead of the key itself
    returnEmptyString: false,
    returnNull: false,
  });

// Export configured i18n instance
export default i18n;

// Helper function to get current language
export const getCurrentLanguage = () => i18n.language || 'en-US';

// Helper function to change language
export const changeLanguage = async (languageCode) => {
  try {
    await i18n.changeLanguage(languageCode);
    localStorage.setItem('i18nextLng', languageCode);
    return true;
  } catch (error) {
    console.error('Error changing language:', error);
    return false;
  }
};

// Helper function to get language name
export const getLanguageName = (code) => {
  const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
  return lang ? lang.nativeName : code;
};

// Helper to check if language is supported
export const isLanguageSupported = (code) => {
  return SUPPORTED_LANGUAGES.some(l => l.code === code);
};